import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Users, Building2, UserPlus, Plus, ArrowRight, Loader2, Inbox,
    Sparkles, Wallet, BarChart3, Copy, Check,
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { adminMembersApi } from "@/lib/api/adminMembers";
import { adminProvidersApi } from "@/lib/api/adminProviders";
import { toast } from "sonner";

function StatCard({ Icon, iconBg, iconColor, label, value, sub }) {
    return (
        <div className="rounded-2xl border border-border bg-card p-5 flex items-start gap-4">
            <div className={`h-11 w-11 rounded-xl ${iconBg} flex items-center justify-center shrink-0`}>
                <Icon className={`h-5 w-5 ${iconColor}`} />
            </div>
            <div className="min-w-0">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</p>
                <p className="text-2xl font-bold tracking-tight mt-0.5">{value}</p>
                {sub && <p className="text-xs mt-0.5 text-muted-foreground">{sub}</p>}
            </div>
        </div>
    );
}

function fmtDate(iso, lang) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-GB", {
            day: "2-digit", month: "short", year: "numeric",
        });
    } catch (_) { return String(iso).slice(0, 10); }
}

const TIER_COLORS = {
    SILVER:   "bg-zinc-100 text-zinc-700",
    GOLD:     "bg-amber-50 text-amber-700",
    PLATINUM: "bg-sky-50 text-sky-700",
};

export default function KioskSalesPage() {
    const { lang } = useI18n();
    const { user } = useAuth();
    const tr = (en, ar) => lang === "ar" ? ar : en;

    const [members, setMembers] = useState(null);
    const [memberTotal, setMemberTotal] = useState(0);
    const [providers, setProviders] = useState(null);
    const [providerTotal, setProviderTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        Promise.all([
            adminMembersApi.list({ limit: 5 }).catch(() => ({ items: [], total: 0 })),
            adminProvidersApi.list({ limit: 5 }).catch(() => ({ items: [], total: 0 })),
        ]).then(([m, p]) => {
            setMembers(m.items || []); setMemberTotal(m.total || 0);
            setProviders(p.items || []); setProviderTotal(p.total || 0);
        }).finally(() => setLoading(false));
    }, []);

    const referralCode = user?.referral_code || "";
    const referralLink = referralCode
        ? `${window.location.origin}/signup?ref=${referralCode}`
        : "";

    const copyLink = () => {
        if (!referralLink) return;
        navigator.clipboard.writeText(referralLink).then(() => {
            setCopied(true);
            toast.success(tr("Referral link copied", "تم نسخ رابط الإحالة"));
            setTimeout(() => setCopied(false), 2500);
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="max-w-4xl space-y-6 fade-up" data-testid="page-kiosk-sales">
            <header>
                <p className="text-sm font-medium text-primary">{tr("Sales Kiosk", "كشك المبيعات")}</p>
                <h1 className="mt-1 text-3xl font-semibold tracking-tight">
                    {tr("Hi, ", "مرحباً، ")}{user?.full_name?.split(" ")[0] || ""}
                </h1>
                <p className="mt-1.5 text-sm text-muted-foreground">
                    {tr("Your sales activity at a glance.", "ملخص نشاط مبيعاتك.")}
                </p>
            </header>

            {/* KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <StatCard
                    Icon={Users}
                    iconBg="bg-primary/10"
                    iconColor="text-primary"
                    label={tr("Members signed up", "أعضاء مسجلون")}
                    value={memberTotal}
                    sub={memberTotal === 0
                        ? tr("Get started below", "ابدأ من الأسفل")
                        : tr("attributed to you", "منسوبة إليك")}
                />
                <StatCard
                    Icon={Building2}
                    iconBg="bg-emerald-50"
                    iconColor="text-emerald-600"
                    label={tr("Providers brought", "مزودون تمت إضافتهم")}
                    value={providerTotal}
                    sub={providerTotal === 0
                        ? tr("Add your first one", "أضف أول مزود")
                        : tr("under your referral", "تحت إحالتك")}
                />
                <StatCard
                    Icon={Wallet}
                    iconBg="bg-amber-50"
                    iconColor="text-amber-600"
                    label={tr("Commission", "العمولة")}
                    value="—"
                    sub={tr("Coming with finance module", "ضمن وحدة المالية القادمة")}
                />
            </div>

            {/* Quick actions */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Link
                    to="/kiosk/register"
                    className="rounded-2xl border-2 border-primary/15 bg-primary/5 hover:border-primary/30 hover:bg-primary/10 transition-all p-5 flex items-start gap-3 group"
                >
                    <div className="h-10 w-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
                        <UserPlus className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground">
                            {tr("Register a new member", "تسجيل عضو جديد")}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            {tr("Sign someone up in person", "سجّل شخصاً مباشرة")}
                        </p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-primary/60 group-hover:text-primary group-hover:translate-x-1 transition-all" />
                </Link>
                <Link
                    to="/kiosk/providers"
                    className="rounded-2xl border-2 border-emerald-200/40 bg-emerald-50/40 hover:border-emerald-300 hover:bg-emerald-50 transition-all p-5 flex items-start gap-3 group"
                >
                    <div className="h-10 w-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                        <Plus className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground">
                            {tr("Add a provider", "إضافة مزود")}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            {tr("Onboard a clinic, pharmacy or hospital", "أضف عيادة أو صيدلية أو مستشفى")}
                        </p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-emerald-700/60 group-hover:text-emerald-700 group-hover:translate-x-1 transition-all" />
                </Link>
            </div>

            {/* Referral link */}
            {referralLink && (
                <div className="rounded-2xl border border-border bg-card p-5">
                    <div className="flex items-start gap-3 mb-3">
                        <div className="h-9 w-9 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                            <Sparkles className="h-4 w-4" />
                        </div>
                        <div className="flex-1">
                            <p className="text-sm font-semibold">{tr("Your referral link", "رابط الإحالة الخاص بك")}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                {tr(
                                    "Share this link — anyone who signs up through it is attributed to you.",
                                    "شارك هذا الرابط — كل من يسجل من خلاله يُنسب إليك."
                                )}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 rounded-xl border border-border bg-muted/30 px-3 py-2">
                        <code className="flex-1 text-xs font-mono truncate text-muted-foreground" dir="ltr">
                            {referralLink}
                        </code>
                        <button
                            onClick={copyLink}
                            className="shrink-0 inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90"
                        >
                            {copied
                                ? <><Check className="h-3.5 w-3.5" />{tr("Copied", "تم النسخ")}</>
                                : <><Copy className="h-3.5 w-3.5" />{tr("Copy", "نسخ")}</>
                            }
                        </button>
                    </div>
                </div>
            )}

            {/* Recent members */}
            <section>
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-base font-semibold inline-flex items-center gap-2">
                        <Users className="h-4 w-4 text-primary" />
                        {tr("Recent members", "أحدث الأعضاء")}
                    </h2>
                    {memberTotal > 0 && (
                        <Link to="/kiosk/members" className="text-xs font-semibold text-primary hover:underline">
                            {tr("View all →", "عرض الكل →")}
                        </Link>
                    )}
                </div>
                {members.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-border bg-card p-8 flex flex-col items-center gap-2 text-center">
                        <Inbox className="h-8 w-8 text-muted-foreground" />
                        <p className="text-xs text-muted-foreground">
                            {tr("No members yet — register your first one!", "لا يوجد أعضاء بعد — سجّل أول عضو!")}
                        </p>
                    </div>
                ) : (
                    <div className="rounded-2xl border border-border bg-card overflow-hidden">
                        {members.map((m, i) => (
                            <div
                                key={m.id}
                                className={`px-5 py-3 flex items-center gap-3 ${i < members.length - 1 ? "border-b border-border/60" : ""}`}
                                data-testid={`kiosk-member-row-${m.id}`}
                            >
                                <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                    <span className="text-sm font-bold text-primary">
                                        {(m.full_name || "?").charAt(0).toUpperCase()}
                                    </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{m.full_name || "—"}</p>
                                    <p className="text-xs text-muted-foreground truncate">
                                        <span className="font-mono">{m.member_code}</span>
                                        <span className="mx-1.5">•</span>
                                        {fmtDate(m.created_at, lang)}
                                    </p>
                                </div>
                                {m.tier && (
                                    <span className={`shrink-0 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${TIER_COLORS[m.tier] || TIER_COLORS.SILVER}`}>
                                        {m.tier}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Recent providers */}
            <section>
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-base font-semibold inline-flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-emerald-600" />
                        {tr("Recent providers", "أحدث المزودين")}
                    </h2>
                    {providerTotal > 0 && (
                        <Link to="/kiosk/providers" className="text-xs font-semibold text-primary hover:underline">
                            {tr("View all →", "عرض الكل →")}
                        </Link>
                    )}
                </div>
                {providers.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-border bg-card p-8 flex flex-col items-center gap-2 text-center">
                        <Inbox className="h-8 w-8 text-muted-foreground" />
                        <p className="text-xs text-muted-foreground">
                            {tr("No providers yet.", "لا يوجد مزودون بعد.")}
                        </p>
                    </div>
                ) : (
                    <div className="rounded-2xl border border-border bg-card overflow-hidden">
                        {providers.map((p, i) => (
                            <div
                                key={p.id}
                                className={`px-5 py-3 flex items-center gap-3 ${i < providers.length - 1 ? "border-b border-border/60" : ""}`}
                            >
                                <div className="h-9 w-9 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                                    <Building2 className="h-4 w-4 text-emerald-700" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{p.corporate_name}</p>
                                    <p className="text-xs text-muted-foreground truncate">
                                        {p.type}
                                        <span className="mx-1.5">•</span>
                                        {fmtDate(p.created_at, lang)}
                                    </p>
                                </div>
                                <span className="shrink-0 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground">
                                    {p.kyc_status}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
